<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import { useOperationAdditionalDataPugStore } from "@/stores/operation-additional-data-pug.store"
import { computed } from "vue"
import { IffRequiredDocuments } from "#components"
import type { components } from "~/assets/api/openapi"

const operationStore = useOperationStore()
const operationAdditionalDataPugStore = useOperationAdditionalDataPugStore()
const REASON_MAX_LENGTH = 1000
const schema = yup.object().shape({
  countryOfEducation: yup.string().when([], {
    is: () => isOZGRequestWithoutEducationState.value,
    then: (schema) => schema.required("Pflichtfeld"),
    otherwise: (schema) => schema.strip(),
  }),
  shipped: yup.boolean().required("Pflichtfeld"),

  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  responsibility: yup.boolean().required("Pflichtfeld"),

  reason: yup.string().when("responsibility", {
    is: false,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  })
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const isOZGRequestWithoutEducationState = computed<boolean>(
  () =>
    !operationStore.findFormDataValueByTextKey<
      components["schemas"]["StringValue"]
    >("gsb.pug.applicationDataFields.educationCountry")?.value
)
const educationCountriesOptions = computed(
  () =>
    operationAdditionalDataPugStore.data?.educationCountries?.map(
      (country) => ({
        value: country.isoCode2,
        label: getCustomOrIffText("countries", country.isoCode2),
      })
    ) || []
)

watch(
  () => state.shipped,
  (value) => {
    if (value === false) {
      state.shippingConfirmationDate = undefined
    }
    operationStore.unsavedChanges = false
  },
  { immediate: true }
)

watch(
  () => state.responsibility,
  (value) => {
    if (value) {
      state.reason = undefined
    }
  }
)

onMounted(async () => {
  if (operationStore.operation?.id) {
    await operationAdditionalDataPugStore.fetchIfNeeded(
      operationStore.operation?.id
    )
    if (
      operationAdditionalDataPugStore.data?.educationCountries?.length === 1 &&
      isOZGRequestWithoutEducationState.value
    ) {
      state.countryOfEducation =
        operationAdditionalDataPugStore.data?.educationCountries?.[0].isoCode2
      await nextTick()
      operationStore.unsavedChanges = false
    }
  }
})
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Eingangsbestätigung"
      />
    </template>

    <IffContentSection
      v-if="isOZGRequestWithoutEducationState"
      headline="Land des Abschlusses festlegen"
    >
      <IffMessage
        v-if="educationCountriesOptions.length > 1"
        variant="info"
        class="mb-5"
      >
        <IffCopyText size="small">
          Die antragstellende Person hat mehr als ein Ausbildungsland angegeben.
          Bitte wählen Sie das Land aus, in dem die Ausbildung abgeschlossen
          wurde.
        </IffCopyText>
      </IffMessage>

      <UFormGroup
        name="countryOfEducation"
        label="Land des Abschlusses"
        required
      >
        <USelect
          v-model="state.countryOfEducation"
          placeholder="Bitte wählen"
          aria-required="true"
          :options="educationCountriesOptions"
          class="max-w-[300px]"
        />
      </UFormGroup>
    </IffContentSection>

    <IffContentSection headline="Nachweise prüfen">
      <UButton
        variant="outline"
        @click="() => operationStore.changeMainView(IffRequiredDocuments)"
      >
        Nachweisliste öffnen
      </UButton>
    </IffContentSection>

    <IffContentSection
      headline="Versand Eingangssachaufklärung"
      :is-last-child="false"
    >
      <UFormGroup name="shipped">
        <IffDualRadioGroup
          legend="Ist die Eingangssachaufklärung erforderlich und ist der Versand an die antragstellende Person erfolgt?"
          v-model="state.shipped"
          required
          aria-required="true"
          aria-controls="shipping-confirmation-date-region"
        />
      </UFormGroup>
      <div id="shipping-confirmation-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            name="shippingConfirmationDate"
            label="Datum Eingangsbestätigung"
            v-if="state.shipped"
            required
            class="max-w-[270px]"
          >
            <template #hint>
              <IffIconTooltip
                text="BQFG: Wert wird in Datum der Empfangsbestätigung (e01) übernommen."
              />

              <UButton
                @click="
                  state.shippingConfirmationDate = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.shippingConfirmationDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
    <IffContentSection
      headline="Zuständigkeit prüfen"
      :is-last-child="true"
    >
      <IffDualRadioGroup
        name="documentsComplete"
        v-model="state.responsibility"
        legend="Ist die Zuständigkeit gegeben?"
        required
        aria-controls="reason-region responsibility-date-region"
      />
      <div id="reason-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.responsibility === false"
            name="reason"
            label="Begründung"
            :help="`Noch ${
              REASON_MAX_LENGTH - (state.reason?.length ?? 0)
            } Zeichen verfügbar`"
            required
            class="max-w-[600px]"
          >
            <UTextarea
              v-model="state.reason"
              :maxlength="REASON_MAX_LENGTH"
              resize
              aria-required="true"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
